// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Project } from '@/project/api/types';
import { ProjectKeyDateField } from '@/project/components/project-key-date-label';

interface KeyDateSequenceEntry {
  field: ProjectKeyDateField;
  label: string;
}

/** Project fields shown in the key dates section, in expected chronological order */
const PROJECT_KEY_DATE_SEQUENCE: KeyDateSequenceEntry[] = [
  { field: 'contractSignedOn', label: 'contract date' },
  { field: 'siteVisitDate', label: 'site visit date' },
  { field: 'installDate', label: 'install date' },
  { field: 'trainingDate', label: 'training date' },
  { field: 'goLiveDate', label: 'go-live date' },
  { field: 'acceptanceDate', label: 'acceptance date' },
];

type ProjectKeyDates = Pick<Project, ProjectKeyDateField>;

/**
 * Returns advisory warnings when a key date falls outside the expected
 * chronological order. Compares against the nearest earlier and nearest
 * later dates that are set; unset dates are skipped.
 */
export function getProjectKeyDateWarnings(
  project: ProjectKeyDates,
  field: ProjectKeyDateField
): string[] {
  const date = project[field];
  if (date === null) {
    return [];
  }

  const index = PROJECT_KEY_DATE_SEQUENCE.findIndex(
    (entry) => entry.field === field
  );
  const isSet = (entry: KeyDateSequenceEntry) => project[entry.field] !== null;

  const previous = PROJECT_KEY_DATE_SEQUENCE.slice(0, index)
    .reverse()
    .find(isSet);
  const following = PROJECT_KEY_DATE_SEQUENCE.slice(index + 1).find(isSet);

  const warnings: string[] = [];

  const previousDate = previous && project[previous.field];
  if (previous && previousDate && date < previousDate) {
    warnings.push(
      `This date is before the ${previous.label}. You might want to check your dates.`
    );
  }

  const followingDate = following && project[following.field];
  if (following && followingDate && date > followingDate) {
    warnings.push(
      `This date is after the ${following.label}. You might want to check your dates.`
    );
  }

  return warnings;
}
